/** 一个收件人 id 的长度上限（飞书 / 钉钉的 id 都是几十字符）。 */
const RECIPIENT_ID_MAX = 200;
/** 样例消息的长度上限（确认卡上看一眼用，不是正文模板）。 */
const SAMPLE_MAX = 2000;
/** 名单人数上限（一条定时任务替员工发消息，几十个收件人已经不是「点名」）。 */
const RECIPIENTS_MAX = 50;
/**
 * 发消息工具的认法（按出现顺序报告给人看）。钉钉 `dws` 的发送子命令形状仓内没有文档（`UNVERIFIED`）：按
 * 「`dws` 命令里有以 send 开头的子命令词」认。
 */
const MESSAGE_SEND_TOOL_PATTERNS = [
    { label: "im +messages-send", re: /(?:^|[\s"'`(=])\+messages-send\b/ },
    { label: "im +messages-reply", re: /(?:^|[\s"'`(=])\+messages-reply\b/ },
    { label: "feishu_im_send", re: /\bfeishu_im_send\b/ },
    { label: "feishu_im_reply", re: /\bfeishu_im_reply\b/ },
    { label: "/open-apis/im/v1/messages", re: /\/open-apis\/im\/v1\/messages\b/ },
    { label: "dws … send", re: /\bdws\b[^\n;|&]*?\bsend(?:-[a-z-]+)?\b/i },
];
/** 一段命令或提示词里出现的发消息工具（去重、按判据表顺序）。非字符串 → []。 */
export function detectMessageSendTools(text) {
    if (typeof text !== "string" || text.length === 0)
        return [];
    return MESSAGE_SEND_TOOL_PATTERNS.filter(({ re }) => re.test(text)).map(({ label }) => label);
}
function isRecord(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
}
/**
 * 读执行配置的 `messageRecipients`：缺省 → `{}`；形状对 → `{ recipients }`（只留认得的键、去首尾空白）；
 * 形状不对 → `{ error }`（中文原因，解析面包成解析错误）。
 */
export function readMessageRecipients(value) {
    if (value === undefined)
        return {};
    if (!isRecord(value)) {
        return { error: "messageRecipients 须为 {to: [...], sample: \"…\"} 对象" };
    }
    if (!Array.isArray(value.to)) {
        return { error: "messageRecipients.to 须为收件人数组（每项 {platform, kind, id, name?}）" };
    }
    if (value.to.length > RECIPIENTS_MAX) {
        return { error: `messageRecipients.to 最多 ${RECIPIENTS_MAX} 个收件人` };
    }
    const to = [];
    for (let i = 0; i < value.to.length; i += 1) {
        const item = value.to[i];
        if (!isRecord(item))
            return { error: `messageRecipients.to[${i}] 须为对象` };
        const { platform, kind, id, name } = item;
        if (platform !== "feishu" && platform !== "dingtalk") {
            return { error: `messageRecipients.to[${i}].platform 只能是 "feishu" 或 "dingtalk"` };
        }
        if (kind !== "chat" && kind !== "user") {
            return { error: `messageRecipients.to[${i}].kind 只能是 "chat"（群）或 "user"（人）` };
        }
        if (typeof id !== "string" || !id.trim() || id.trim().length > RECIPIENT_ID_MAX) {
            return { error: `messageRecipients.to[${i}].id 须为非空字符串（群 id / 人的 id）` };
        }
        if (name !== undefined && typeof name !== "string") {
            return { error: `messageRecipients.to[${i}].name 须为字符串` };
        }
        const recipient = { platform, kind, id: id.trim() };
        if (typeof name === "string" && name.trim())
            recipient.name = name.trim();
        to.push(recipient);
    }
    if (typeof value.sample !== "string" || value.sample.length > SAMPLE_MAX) {
        return { error: `messageRecipients.sample 须为一条样例消息（字符串，最长 ${SAMPLE_MAX} 字）` };
    }
    return { recipients: { to, sample: value.sample.trim() } };
}
/** 执行配置里会被执行的命令 / 提示词文本（判「会不会发消息」用）。modelCall 的模型调用零工具，只看预取命令。 */
function executionSendTexts(execution) {
    switch (execution.kind) {
        case "agentTurn":
            return [execution.prompt, execution.precheck?.command ?? ""];
        case "script":
            return [execution.command];
        case "modelCall":
            return [execution.command ?? ""];
        default:
            return [];
    }
}
/**
 * 一次修改有没有改到「会被执行的命令 / 提示词」（判「下次修改时要求补名单」用，维护者 2026-10-26 拍板 D2=A）。
 * 只换模型、超时、工具面等不改这些文本的修改 → false：任务详情「更换模型」这类按钮改不了名单，不能因此把存量任务卡死
 * （与云端 N-30 R2「只在新设或换掉 Agent 时判」同一姿态）。换了执行形态 → 文本必然不同 → true。
 */
export function messageSendTextsChanged(before, after) {
    if (before.kind !== after.kind)
        return true;
    const a = executionSendTexts(before);
    const b = executionSendTexts(after);
    return a.length !== b.length || a.some((text, i) => text !== b[i]);
}
function declaredRecipients(execution) {
    return execution.kind === "agentTurn" || execution.kind === "script" || execution.kind === "modelCall"
        ? execution.messageRecipients
        : undefined;
}
/**
 * 会发消息却没带（有效的）收件人名单时的拒绝原因；不发消息或已带名单 → null。
 * `extraTexts`：执行配置之外、同样会被执行的文本（企业任务设备腿 agentTurn 执行的是任务顶层的 `prompt`）。
 */
export function missingMessageRecipientsReason(execution, extraTexts = []) {
    const tools = [...new Set([...executionSendTexts(execution), ...extraTexts].flatMap((t) => detectMessageSendTools(t)))];
    if (tools.length === 0)
        return null;
    const declared = declaredRecipients(execution);
    if (declared && declared.to.length > 0 && declared.sample.length > 0)
        return null;
    const what = declared && declared.to.length > 0 ? "一条样例消息（messageRecipients.sample）" : "收件人名单和一条样例消息";
    return (`这个任务会替你发消息（用到 ${tools.join("、")}），需要先列出${what}，确认后写进 execution.messageRecipients ` +
        "= {to:[{platform:'feishu'|'dingtalk', kind:'chat'|'user', id, name}], sample:'<一条样例消息>'}；" +
        "无人值守运行时只会发给名单里的人。");
}
/** 收件人给人看的写法：名字（id）或只有 id。 */
export function describeRecipient(r) {
    const where = r.platform === "feishu" ? (r.kind === "chat" ? "飞书群" : "飞书") : r.kind === "chat" ? "钉钉群" : "钉钉";
    return r.name ? `${where}「${r.name}」（${r.id}）` : `${where} ${r.id}`;
}
/** 一个（平台 + 群还是人 + id）收件人在不在名单里：逐字比对，不做 id 换算。 */
export function isRecipientInList(list, target) {
    return list.to.some((r) => r.platform === target.platform && r.kind === target.kind && r.id === target.id);
}
//# sourceMappingURL=message-recipients.js.map